import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
  Badge,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  ExpandLess,
  ExpandMore,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  EventAvailable as EventIcon,
  School as SchoolIcon,
  LocalHospital as HospitalIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingIcon,
  Assessment as AssessmentIcon,
  MonetizationOn as CostIcon,
  Description as DescriptionIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  Handyman as HandymanIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 280;

const menuItems = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />,
  },
  {
    title: 'RRHH',
    icon: <PeopleIcon />,
    children: [
      { title: 'Panel RRHH', path: '/rrhh', icon: <DashboardIcon /> },
      { title: 'Empleados', path: '/rrhh/empleados', icon: <BadgeIcon /> },
      { title: 'Asistencia', path: '/rrhh/asistencia', icon: <EventIcon /> },
      { title: 'Licencias', path: '/rrhh/licencias', icon: <EventIcon /> },
      { title: 'Capacitaciones', path: '/rrhh/capacitaciones', icon: <SchoolIcon /> },
      { title: 'Control Médico', path: '/rrhh/control-medico', icon: <HospitalIcon /> },
      { title: 'EPP', path: '/rrhh/epp', icon: <SecurityIcon /> },
      { title: 'Turnos', path: '/rrhh/turnos', icon: <ScheduleIcon /> },
    ],
  },
  {
    title: 'Finanzas',
    icon: <MoneyIcon />,
    children: [
      { title: 'Panel Finanzas', path: '/finanzas', icon: <DashboardIcon /> },
      { title: 'Impuestos', path: '/finanzas/impuestos', icon: <ReceiptIcon /> },
      { title: 'Cuentas Bancarias', path: '/finanzas/cuentas-bancarias', icon: <BankIcon /> },
      { title: 'Pagos y Cobranzas', path: '/finanzas/pagos-cobranzas', icon: <TrendingIcon /> },
      { title: 'Presupuestos', path: '/finanzas/presupuestos', icon: <AssessmentIcon /> },
      { title: 'Costos por Obra', path: '/finanzas/costos-obra', icon: <CostIcon /> },
      { title: 'Liquidaciones', path: '/finanzas/liquidaciones', icon: <DescriptionIcon /> },
    ],
  },
  {
    title: 'Administración',
    icon: <BusinessIcon />,
    children: [
      { title: 'Panel Admin', path: '/administracion', icon: <DashboardIcon /> },
      { title: 'Facturación', path: '/administracion/facturacion', icon: <ReceiptIcon /> },
      { title: 'Contratos', path: '/administracion/contratos', icon: <DescriptionIcon /> },
      { title: 'Proveedores', path: '/administracion/proveedores', icon: <ShippingIcon /> },
      { title: 'Órdenes de Compra', path: '/administracion/ordenes-compra', icon: <AssignmentIcon /> },
      { title: 'Stock', path: '/administracion/stock', icon: <InventoryIcon /> },
      { title: 'Partes Diarios', path: '/administracion/partes-diarios', icon: <AssignmentIcon /> },
    ],
  },
  {
    title: 'Operaciones',
    icon: <EngineeringIcon />,
    children: [
      { title: 'Panel Operaciones', path: '/operaciones', icon: <DashboardIcon /> },
      { title: 'Órdenes de Trabajo', path: '/operaciones/ordenes-trabajo', icon: <BuildIcon /> },
      { title: 'Equipos', path: '/operaciones/equipos', icon: <HandymanIcon /> },
      { title: 'Mantenimiento', path: '/operaciones/mantenimiento', icon: <BuildIcon /> },
      { title: 'Incidentes HSE', path: '/operaciones/incidentes-hse', icon: <WarningIcon /> },
      { title: 'Avances', path: '/operaciones/avances', icon: <TimelineIcon /> },
    ],
  },
];

const Layout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (title) => {
    setOpenMenus((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleProfileMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseProfile = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          backgroundColor: '#3a3a3a',
        }}
      >
        <img
          src="https://petrolatina.com.ar/wp-content/uploads/2025/03/cropped-IMG_1195-removebg-preview-300x241.png"
          alt="Petrolatina"
          style={{ height: 50, objectFit: 'contain' }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography
              component="span"
              sx={{
                fontWeight: 300,
                fontSize: '1.4rem',
                color: '#ffffff',
                letterSpacing: '0.15em',
                fontFamily: '"Inter", sans-serif',
              }}
            >
              PETRO
            </Typography>
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                fontSize: '1.4rem',
                color: '#76b3b7',
                letterSpacing: '0.15em',
                fontFamily: '"Inter", sans-serif',
              }}
            >
              LATINA
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '0.6rem',
              color: alpha('#ffffff', 0.6),
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              fontWeight: 400,
              mt: -0.3,
            }}
          >
            Servicios Petroleros
          </Typography>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <List component="nav">
          {menuItems.map((item) => (
            <React.Fragment key={item.title}>
              {item.children ? (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleMenuClick(item.title)}
                      sx={{
                        mx: 1,
                        borderRadius: 2,
                        mb: 0.5,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: theme.palette.primary.main,
                          minWidth: 40,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          fontWeight: 500,
                          fontSize: '0.95rem',
                        }}
                      />
                      {openMenus[item.title] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={openMenus[item.title]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => (
                        <ListItem key={child.path} disablePadding>
                          <ListItemButton
                            onClick={() => handleNavigation(child.path)}
                            sx={{
                              pl: 4,
                              mx: 1,
                              borderRadius: 2,
                              mb: 0.5,
                              backgroundColor: isActivePath(child.path)
                                ? alpha(theme.palette.primary.main, 0.12)
                                : 'transparent',
                              borderLeft: isActivePath(child.path)
                                ? `3px solid ${theme.palette.secondary.main}`
                                : '3px solid transparent',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: isActivePath(child.path)
                                  ? theme.palette.secondary.main
                                  : theme.palette.text.secondary,
                                minWidth: 36,
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.title}
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                                fontWeight: isActivePath(child.path) ? 600 : 400,
                                color: isActivePath(child.path)
                                  ? theme.palette.primary.main
                                  : theme.palette.text.primary,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      mx: 1,
                      borderRadius: 2,
                      mb: 0.5,
                      backgroundColor: isActivePath(item.path)
                        ? alpha(theme.palette.primary.main, 0.12)
                        : 'transparent',
                      borderLeft: isActivePath(item.path)
                        ? `3px solid ${theme.palette.secondary.main}`
                        : '3px solid transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActivePath(item.path)
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                        minWidth: 40,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontWeight: isActivePath(item.path) ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* User Info Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.secondary.main,
              fontSize: '1rem',
            }}
          >
            {user?.nombre?.[0]}{user?.apellido?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {user?.nombre} {user?.apellido}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.rol}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: theme.palette.primary.main }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {/* Notifications */}
          <Tooltip title="Notificaciones">
            <IconButton
              onClick={(e) => setNotificationAnchor(e.currentTarget)}
              sx={{ color: theme.palette.text.secondary }}
            >
              <Badge badgeContent={5} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Settings */}
          <Tooltip title="Configuración">
            <IconButton sx={{ color: theme.palette.text.secondary, mx: 1 }}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {/* Profile Menu */}
          <Tooltip title="Mi Perfil">
            <IconButton onClick={handleProfileMenu}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '0.9rem',
                }}
              >
                {user?.nombre?.[0]}{user?.apellido?.[0]}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseProfile}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: { mt: 1, minWidth: 200 },
            }}
          >
            <MenuItem onClick={handleCloseProfile}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Mi Perfil
            </MenuItem>
            <MenuItem onClick={handleCloseProfile}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Configuración
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <Typography color="error">Cerrar Sesión</Typography>
            </MenuItem>
          </Menu>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={() => setNotificationAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: { mt: 1, minWidth: 320, maxHeight: 400 },
            }}
          >
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Notificaciones
              </Typography>
            </Box>
            {[
              { text: 'Capacitación H2S vence en 5 días - Juan Rodríguez', type: 'warning' },
              { text: 'Apto médico vencido - Pedro García', type: 'error' },
              { text: 'Nueva orden de compra pendiente de aprobación', type: 'info' },
              { text: 'Presupuesto Pozo XY-123 excedido en 15%', type: 'warning' },
              { text: 'Incidente HSE registrado en Yacimiento Norte', type: 'error' },
            ].map((notification, index) => (
              <MenuItem key={index} onClick={() => setNotificationAnchor(null)}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <WarningIcon
                    fontSize="small"
                    color={notification.type}
                    sx={{ mt: 0.5 }}
                  />
                  <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                    {notification.text}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: '#f5f7fa',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
